import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import parse from 'html-react-parser';
import API from '../services/API';
import utils from '../services/Utils';

// wtRegion constants
const US = 'US';
const EU = 'EU';

// Set up US or EU date format flag based on the wtRegion React App Variable from sitecore
// Default to EU if not present.
const dateFormat = window.performanceChart && window.performanceChart.wtRegion && window.performanceChart.wtRegion === US ? US : EU;

const formatDate = (dtStr) => {
  const dt = new Date(dtStr);

  if (dateFormat === US) {
    return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
  }
  return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
};

const getFullYear = (value) => {
  const date = new Date(value);
  return date.getFullYear();
};

const getISIN = () => {
  const tableData = utils.parseHTMLTableFromFundOverviewSection();
  if (!tableData) {
    return utils.parseHTMLTableFromListingSection()?.ISIN;
  }
  return tableData.ISIN;
};

const formatToTwoDecimalPlaces = (num) => (num.toFixed(4) * 100).toFixed(2);

const getOptions = (data) => {
  const { idxBmkName } = data.find(d => d.idxBmkName);
  const { productName } = data.find(d => d.productName);
  const option = {
    grid: { top: 30, right: 8, bottom: 85, left: 64 },
    color: ['#05a9b1', '#0047BB'],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          formatter: (params) => {
            if (params.axisDimension === 'y') {
              return `${params.value.toFixed(2)}%`;
            }
            return `${formatDate(params.value)}`;
          },
        },
      },
      valueFormatter: (value) => `${value}%`,
      textStyle: {
        color(value) {
          if (value < 0) {
            return '#dc3545';
          }
          return '#000';
        },
      },
    },
    legend: {
      show: true,
      data: [productName, idxBmkName],
      top: 'bottom',
      textStyle: {
        fontWeight: 'bolder',
      },
    },
    xAxis: [
      {
        type: 'category',
        data: data.map(x => x.dt),
        axisLabel: {
          rotate: 45,
          formatter: getFullYear,
          fontWeight: 'bold',
        },
        boundaryGap: true,
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'Percentage',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyleObject: {
          fontSize: 20,
        },
        axisLabel: {
          formatter(value) {
            return `${value}.0%`;
          },
          textStyle: {
            color(value) {
              if (value < 0) {
                return '#dc3545';
              }
              return '#000';
            },
          },
        },
      },
    ],
    series: [
      {
        name: productName,
        type: 'bar',
        data: data.map(x => x.product),
        symbol: 'circle',
        barMaxWidth: 30,
      },

      {
        name: idxBmkName,
        type: 'bar',
        data: data.map(x => x.idxBmk),
        symbol: 'circle',
        barMaxWidth: 30,
      },

    ],
  };

  return option;
};

const PerformanceChart = () => {
  const [options, setOptions] = useState(null);
  const { title = '', disclosure = '' } = window.performanceChart;

  useEffect(() => {
    const fetchOptions = async () => {
      const isin = await getISIN();
      if (isin) {
        const response = await API.getPerformanceChartData(isin);
        if (Array.isArray(response)) {
          const updatedData = await response.map(d => ({
            ...d,
            product: formatToTwoDecimalPlaces(d.product),
            idxBmk: formatToTwoDecimalPlaces(d.idxBmk),
          }));
          setOptions(getOptions(updatedData));
        }
      }
    };
    fetchOptions();
  }, []);

  return (
    <div>
      {options && (
        <>
          <div className="title">{parse(title)}</div>
          <ReactECharts
            option={options}
            notMerge
            lazyUpdate
            onChartReady={() => { }}
            onEvents={() => { }}
            style={{
              width: '100%',
            }}
            opts={{ renderer: 'svg' }}
          />
          <div className="disclosure">{parse(disclosure)}</div>
        </>
      )}
    </div>
  );
};

export default PerformanceChart;
